// combatpro/contexts/MonsterSelectionContext.js
//
// Combat PRO - Encounter Monster Selection runtime (Stage 61: Encounter Monster
// Selection Runtime Migration).
//
// The React runtime layer around the pure monsterSelectionEngine. It is the single
// authoritative owner of:
//   - the runtime custom monster pool (NOT a second storage system - the selection
//     pool consumed by getAllMonsters, fed by later integrations via setCustomPool);
//   - the selectionVersion counter (the declarative equivalent of the legacy DOM
//     refreshes - updateMonsterDropdowns / vi_refreshMonsterSearchData /
//     updateEncounterSummary - modelled on the existing spellLibraryVersion pattern
//     in CombatContext: a bump re-renders consumers, which read fresh data);
//   - the single encounter insertion path, selectMonster.
//
// It does NOT own editing, persistence, Monster Studio, the encounter UI, or
// CombatContext architecture. CombatContext is not modified: this provider reads the
// already-exposed state + setState from CombatContext via useContext and commits a
// built slot with the same immutable monsters.map pattern CombatContext itself uses
// (updateMonsterField / applyDamage). The provider mounts INSIDE CombatProvider so
// that context is available.
//
// selectMonster (battletracker.html 6672) here performs only the state half: look
// the monster up in the combined pool (official window.VI_MONSTERS_DB + the runtime
// custom pool), build a complete slot from the current slot via the pure engine,
// commit it through setState, and bump selectionVersion. The legacy DOM writes,
// refresh* calls, dropdown rebuilds, and saveState are intentionally not migrated -
// panels render from state.monsters and the spell selects already follow the
// version pattern.
//
// Official monsters come from window.VI_MONSTERS_DB and the ability lookup from
// window.VI_ABILITIES_DB (the same window.VI_* convention the rest of the app uses);
// no parallel loader is created and no missing DB structure is invented (a missing
// global simply yields an empty list).
//
// No DOM, no querySelector, no innerHTML, no fetch, no localStorage. No optional
// chaining. No em dash.

import React, { createContext, useState, useContext, useCallback } from 'react';
import { CombatContext } from './CombatContext';
import {
  getAllMonsters as engineGetAllMonsters,
  findMonster,
  buildSelectedSlot
} from '../monster/monsterSelectionEngine';

export const MonsterSelectionContext = createContext(null);

function readOfficialDb() {
  return (typeof window !== 'undefined' && Array.isArray(window.VI_MONSTERS_DB)) ? window.VI_MONSTERS_DB : [];
}

function readAbilitiesDb() {
  return (typeof window !== 'undefined' && Array.isArray(window.VI_ABILITIES_DB)) ? window.VI_ABILITIES_DB : [];
}

export function MonsterSelectionProvider(props) {
  const combat = useContext(CombatContext);
  const [customPool, setCustomPoolState] = useState([]);
  const [selectionVersion, setSelectionVersion] = useState(0);

  // The combined official + custom pool (getAllMonsters). Recomputed from the live
  // custom pool; consumers re-read it whenever the pool or selectionVersion changes.
  const getAllMonsters = useCallback(function getAllMonstersRuntime() {
    return engineGetAllMonsters(readOfficialDb(), customPool);
  }, [customPool]);

  // Feed the runtime pool (the seam later integrations use, e.g. the Stage 62 studio
  // bridge). Bumps the version so selection consumers refresh declaratively.
  const setCustomPool = useCallback(function setCustomPoolRuntime(next) {
    setCustomPoolState(next);
    setSelectionVersion(function(v) { return v + 1; });
  }, []);

  // The single authoritative encounter insertion path (selectMonster). Builds a
  // complete slot from the current slot and commits it; no-op when the name is not
  // found or the index is out of range.
  const selectMonster = useCallback(function selectMonsterRuntime(index, monsterName) {
    if (!combat || typeof combat.setState !== 'function') { return false; }
    var monster = findMonster(monsterName, readOfficialDb(), customPool);
    if (!monster) { return false; }
    combat.setState(function(previous) {
      var monsters = Array.isArray(previous.monsters) ? previous.monsters : [];
      if (index < 0 || index >= monsters.length) { return previous; }
      var nextMonsters = monsters.map(function(slot, i) {
        return i === index ? buildSelectedSlot(slot, monster, readAbilitiesDb()) : slot;
      });
      return Object.assign({}, previous, { monsters: nextMonsters });
    });
    setSelectionVersion(function(v) { return v + 1; });
    return true;
  }, [combat, customPool]);

  const value = {
    customPool: customPool,
    setCustomPool: setCustomPool,
    selectionVersion: selectionVersion,
    getAllMonsters: getAllMonsters,
    selectMonster: selectMonster
  };

  return (
    <MonsterSelectionContext.Provider value={value}>
      {props.children}
    </MonsterSelectionContext.Provider>
  );
}
